import { GatsbyImage } from "gatsby-plugin-image";
import React, { useEffect } from "react";
import HomeButton from "./button";
import { useSpring, animated } from "@react-spring/web";
import Image from "gatsby-plugin-sanity-image";

const HomeHero = ({ data }) => {
  const words = data.title ? data.title.split(" ") : [];
  const firstLine = words.slice(0, 2).join(" ");
  const secondLine = words.slice(2, 4).join(" ");
  const thirdLine = words.slice(4).join(" ");

  // Parallax spring animations with immediate response
  const [{ scrollY }, setScrollY] = useSpring(() => ({
    scrollY: 0,
    immediate: true,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      clamp: false,
    },
  }));

  // Multiple parallax layers with different speeds
  const backgroundSpring = useSpring({
    transform: scrollY.to((y) => `translate3d(0, ${y * 0.07}px, 0)`),
    immediate: true,
  });

  const overlaySpring = useSpring({
    transform: scrollY.to((y) => `translate3d(0, ${y * 0.5}px, 0)`),
    immediate: true,
  });

  const contentSpring = useSpring({
    transform: scrollY.to((y) => `translate3d(0, ${y * -0.03}px, 0)`),
    immediate: true,
  });

  // Handle scroll with RAF for smooth performance
  useEffect(() => {
    let frame = 0;

    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setScrollY({ scrollY: window.scrollY });
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(frame);
    };
  }, [setScrollY]);

  return (
    <section className="h-full md:aspect-[436/301] w-full relative overflow-hidden">
      {/* Overlay with parallax */}
      <animated.div
        className="absolute hidden md:block top-0 left-0 right-0 bottom-0 z-0 bg-black"
        style={{
          opacity: data?.overlay ? data.overlay : 0.2,
          ...overlaySpring,
        }}
      />

      {/* Desktop Background Image with Parallax */}
      <animated.div
        className="absolute hidden md:block inset-0 w-full h-full object-cover z-[-1]"
        style={backgroundSpring}
      >
        <Image
          asset={data.image.image.asset}
          crop={data.image.image.crop}
          hotspot={data.image.image.hotspot}
          alt={data.image.altText}
          objectFit="cover"
          loading="eager"
          className="w-full h-[100%] object-cover"
        />
      </animated.div>

      {/* Content Container with inverse parallax */}
      <animated.div
        className={`${
          data?.stackable
            ? "flex-col md:justify-center gap-y-3 md:gap-y-8"
            : "flex-col md:justify-center gap-y-5 md:gap-y-0 md:flex-row md:items-center gap-x-2"
        } flex h-full z-10 relative px-section pb-0 pt-[12vh] md:py-[10vh]`}
        style={contentSpring}
      >
        {/* Left */}
        <div className="w-full">
          <h1 className="text-black md:text-white text-5xl sm:text-6xl md:text-7xl lg:text-9xl font-bold tracking-tight leading-[1.1]">
            {firstLine}
            {secondLine && <br />}
            {secondLine}
            {thirdLine && <br />}
            {thirdLine}
          </h1>
          <div className="w-full max-w-4xl mt-8">
            {data.description && (
              <p
                className="text-black mb-4 md:text-white standard-type-p1 font-light text-wrap-pretty mt-5"
                style={{ textShadow: "0 0 10px rgba(0, 0, 0, 0.25)" }}
              >
                {data.description}
              </p>
            )}
            {data.enableButton && data.button?.buttonText && (
              <HomeButton button={data.button} white={true} />
            )}
          </div>
        </div>

        {/* Mobile Background Image (no parallax on mobile) */}
        <div className="block md:hidden inset-0 w-full h-full object-cover z-[-1] max-md:object-[15%]">
          <GatsbyImage
            image={data.image.image.asset.gatsbyImageData}
            alt={data.image.altText}
            objectFit="cover"
            loading="eager"
            className="w-full h-full object-cover"
          />
        </div>
      </animated.div>
    </section>
  );
};

export default HomeHero;
